<template>
  <b-card no-body class="border-0">
    <b-card-body class="p-0">
      <h5 class="mb-3 text-muted text-uppercase font-weight-bold small"><i class="fa fa-comments"></i> Forum Management</h5>

      <template v-if="userData">
        <!-- PRIMARY GROUP -->
        <b-form-group label="Primary Group" label-for="primary-group">
          <b-input-group>
            <b-form-select
              id="primary-group"
              v-model="localPrimaryGroupId"
              :options="groupSelectOptions"
              :disabled="isBusy || userLock"
            />


            <b-input-group-append>
              <b-button
                variant="primary"
                :disabled="!isPrimaryDirty || isBusy || userLock"
                @click="applyPrimary"
              >
                <b-spinner small class="mr-1" v-if="isSavingPrimary" />
                Change
              </b-button>
              <b-button
                variant="outline-secondary"
                :disabled="!isPrimaryDirty || isBusy || userLock"
                @click="resetPrimary"
              >
                Reset
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <small v-if="currentPrimaryDisabled" class="text-muted">
            Current role is not selectable (shown disabled).
          </small>
        </b-form-group>

        <!-- SECONDARY GROUPS (editable: managed only) -->
        <group-selection
          v-model="localSecondaryManagedNames"
          :options="managedSecondaryNames"
          :disabled="isBusy || userLock"
          class="mb-2"
        />

        <!-- Actions for secondary -->
        <div class="d-flex">
          <b-button
            variant="primary"
            class="mr-2"
            :disabled="!isSecondaryDirty || isBusy || userLock"
            @click="applySecondary"
          >
            <b-spinner small class="mr-1" v-if="isSavingSecondary" />
            Change
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="!isSecondaryDirty || isBusy || userLock"
            @click="resetSecondary"
          >
            Reset
          </b-button>
        </div>

        <!-- Read-only secondary roles (non-managed; preserved on save) -->
        <div v-if="readOnlySecondaryNames.length" class="mt-3">
          <small class="text-muted d-block mb-1">Other secondary roles (read-only)</small>
          <b-badge
            v-for="tag in readOnlySecondaryNames"
            :key="tag"
            variant="secondary"
            class="mr-1"
          >
            {{ tag }}
          </b-badge>
        </div>
      </template>
      <template v-else>
        <div class="text-center py-4 text-muted">
          <i class="fa fa-info-circle"></i> No Forum user data available.
        </div>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
// Child
import GroupSelection from "./SecondaryGroupSelection.vue"
import { mapGetters } from 'vuex'
import iziToast from 'izitoast'

export default {
  name: "Forum",
  components: { GroupSelection },
  props: {
    userData: { type: Object, required: true },
    userLock: { type: Boolean, default: false }, // parent can lock while saving
    servers: { type: Array, default: () => [] }
  },
  data () {
    return {
      // Primary catalogue (for labels). Add what you want visible in the dropdown.
      groupCatalogue: [
        { id: 3,  name: "Registered User" },
        { id: 6,  name: "Moderator" },
        { id: 9,  name: "Artist" },
        { id: 11, name: "Content Scripter" },
        { id: 12, name: "Game Master" },
        { id: 13, name: "Community Coordinator" },
        { id: 14, name: "Staff Apprentice" },
        { id: 15, name: "Suspended" },
        { id: 17, name: "Former Staff" },
        { id: 18, name: "Contributor" },
        { id: 19, name: "Discord Moderator" },
        { id: 21, name: "Trade Moderator" },
        { id: 23, name: "Spawn Editor" },
        { id: 25, name: "Mapper" },
        { id: 28, name: "Tester" },
        { id: 36, name: "Story Writer" },
        { id: 37, name: "PVP Coordinator" }
      ],

      // Only these primary IDs can be set (others show disabled if current)
      allowedPrimaryIds: [3,6,9,11,12,13,14,15,17,18,19,21,23,25,28,36,37],

      // Managed secondary roles (editable)
      secondaryCatalogue: [
        { id: 16, name: "Management" },
        { id: 26, name: "Donator" },
        { id: 27, name: "PRO Member" }
      ],

      // Local state
      localPrimaryGroupId: null,
      localSecondaryManagedNames: [],

      // UI state
      isSavingPrimary: false,
      isSavingSecondary: false
    }
  },
  computed: {
    isBusy () { return this.isSavingPrimary || this.isSavingSecondary },
    ...mapGetters([
      'forumGroupUpdate'
    ]),
    managedSecondaryNames () {
      return this.secondaryCatalogue.map(g => g.name)
    },

    // Initial (from props) managed names for dirty comparison
    initialManagedNames () {
      const managed = new Set(this.managedSecondaryNames)
      const sec = Array.isArray(this.userData?.secondaryGroups) ? this.userData.secondaryGroups : []
      const names = sec.map(x => (typeof x === 'string' ? x : x?.name)).filter(Boolean)
      return names.filter(n => managed.has(n))
    },

    // Non-managed roles (read-only)
    readOnlySecondaryNames () {
      const managed = new Set(this.managedSecondaryNames)
      const sec = Array.isArray(this.userData?.secondaryGroups) ? this.userData.secondaryGroups : []
      const names = sec.map(x => (typeof x === 'string' ? x : x?.name)).filter(Boolean)
      return names.filter(n => !managed.has(n))
    },

    // Primary select options:
    // - only allowed IDs are selectable
    // - if current primary is unknown/not allowed, prepend a disabled option so it displays
    groupSelectOptions () {
      const allowed = new Set(this.allowedPrimaryIds)
      const opts = this.groupCatalogue
        .filter(g => allowed.has(g.id))
        .map(g => ({ value: g.id, text: g.name })) // selectable

      const curr = this.userData?.primaryGroup
      const currId = curr?.id
      if (curr && !opts.some(o => o.value === currId)) {
        // show current as disabled (client may not know this group)
        opts.unshift({
          value: currId,
          text: curr.name || `Group #${currId}`,
          disabled: true
        })
      }
      return opts
    },

    currentPrimaryDisabled () {
      const currId = this.userData?.primaryGroup?.id
      return !this.allowedPrimaryIds.includes(currId)
    },

    isPrimaryDirty () {
      return (this.localPrimaryGroupId ?? null) !== (this.userData?.primaryGroup?.id ?? null)
    },

    isSecondaryDirty () {
      const a = new Set(this.localSecondaryManagedNames)
      const b = new Set(this.initialManagedNames)
      if (a.size !== b.size) return true
      for (const v of a) if (!b.has(v)) return true
      return false
    }
  },
  created () {
    this.initLocalFromProps()
  },
  watch: {
    userData: {
      deep: true,
      handler () { this.initLocalFromProps() }
    },
    forumGroupUpdate (val) {
      this.isSavingPrimary = false
      this.isSavingSecondary = false
      if (!val.ok) {
        iziToast.error({'title': "Forum group change", 'message': val.message})
        return;
      }
      iziToast.success({'title': "Forum group change", 'message': val.message})

      this.userData.primaryGroup = val.primaryGroupId
      this.userData.secondaryGroups = val.secondaryGroupIds
      this.initLocalFromProps()
    }
  },
  methods: {
    initLocalFromProps () {
      // primary
      this.localPrimaryGroupId = this.userData?.primaryGroup?.id ?? null
      // managed secondaries by NAME
      this.localSecondaryManagedNames = [...this.initialManagedNames]
    },

    resetPrimary () {
      this.localPrimaryGroupId = this.userData?.primaryGroup?.id ?? null
    },

    resetSecondary () {
      this.localSecondaryManagedNames = [...this.initialManagedNames]
    },

    applyPrimary () {
      const allowed = new Set(this.allowedPrimaryIds)
      if (!allowed.has(this.localPrimaryGroupId)) return
      this.isSavingPrimary = true
      console.log({
        memberId: this.userData.id,
        groupId: this.localPrimaryGroupId
      })
      this.emit('setPrimaryGroup', {
        memberId: this.userData.id,
        groupId: this.localPrimaryGroupId
      })
    },

    applySecondary () {
      // Map managed names -> IDs
      const nameToId = new Map(this.secondaryCatalogue.map(({ id, name }) => [name, id]))
      const managedIds = [...new Set(
        this.localSecondaryManagedNames.map(n => nameToId.get(n)).filter(id => id != null)
      )]

      // Preserve current non-managed IDs from userData
      const managedNameSet = new Set(this.managedSecondaryNames)
      const sec = Array.isArray(this.userData?.secondaryGroups) ? this.userData.secondaryGroups : []
      const nonManagedIds = []
      for (const item of sec) {
        const id = typeof item === 'number' ? item : item?.id
        const name = typeof item === 'string' ? item : item?.name
        if (id != null && name && !managedNameSet.has(name)) nonManagedIds.push(id)
      }

      // Final full list = non-managed + chosen managed (deduped)
      const full = Array.from(new Set([...managedIds]))

      this.isSavingSecondary = true
      this.emit('setSecondaryGroups', {
        memberId: this.userData.id,
        groups: full
      })
    }
  }
}
</script>

<style scoped>
.badge + .badge { margin-left: .25rem; }
</style>
